import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Film, Loader2, Check, ArrowRight, RotateCcw, X, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

const REGIONS = ["Asia", "Europe", "Africa", "North America", "South America", "Oceania"];

export default function FestivalSubmit() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const cloneId = sp.get("clone") || "";
  const [festivals, setFestivals] = useState([]);
  const [step, setStep] = useState(user?.is_filmmaker || user?.is_admin ? 1 : 0);
  const [parentSub, setParentSub] = useState(null);
  // Filmmaker apply
  const [bio, setBio] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");
  const [applying, setApplying] = useState(false);
  // Submission
  const [festivalId, setFestivalId] = useState("");
  const [title, setTitle] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [runtime, setRuntime] = useState(0);
  const [region, setRegion] = useState("Asia");
  const [language, setLanguage] = useState("en");
  const [genres, setGenres] = useState("");
  const [trailerId, setTrailerId] = useState("");
  const [posterUrl, setPosterUrl] = useState("");
  const [filmPath, setFilmPath] = useState("");
  const [collabs, setCollabs] = useState([]); // [{user_id, name, role}]
  const [collabEmail, setCollabEmail] = useState("");
  const [collabRole, setCollabRole] = useState("co-director");
  const [collabAdding, setCollabAdding] = useState(false);
  const [posterUploading, setPosterUploading] = useState(false);
  const [filmUploading, setFilmUploading] = useState(false);
  const [filmProg, setFilmProg] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const filmInputRef = useRef(null);
  const posterInputRef = useRef(null);

  useEffect(() => {
    api.get("/festival/festivals").then((r) => {
      setFestivals(r.data || []);
      if (r.data?.[0]) setFestivalId(r.data[0].festival_id);
    });
  }, []);

  // Pre-fill from a previous (rejected/flagged) submission
  useEffect(() => {
    if (!cloneId) return;
    api.get(`/festival/submissions/mine/${cloneId}`).then(({ data }) => {
      setParentSub(data);
      setFestivalId(data.festival_id || "");
      setTitle(data.title || "");
      setSynopsis(data.synopsis || "");
      setRuntime(data.runtime_min || 0);
      setRegion(data.region || "Asia");
      setLanguage(data.language || "en");
      setGenres((data.genres || []).join(", "));
      setTrailerId(data.youtube_trailer_id || "");
      setPosterUrl(data.poster_url || "");
      setFilmPath(data.film_path || "");
      setCollabs((data.collaborators || []).map((c) => ({ user_id: c.user_id, name: c.name, role: c.role })));
      // Skip filmmaker apply step on resubmission
      setStep(1);
    }).catch(() => toast.error("Could not load original submission"));
  }, [cloneId]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post("/festival/become-filmmaker", { bio, country, website });
      await refresh();
      toast.success("Welcome, filmmaker!");
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Application failed");
    } finally { setApplying(false); }
  };

  const uploadFile = async (file, kind, setProg) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem("cc_token") || "";
      xhr.open("POST", `${process.env.REACT_APP_BACKEND_URL || ""}/api/festival/upload`);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && setProg) setProg(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
        else reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(fd);
    });
  };

  const onPosterChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterUploading(true);
    try {
      const r = await uploadFile(file, "festival_poster");
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${r.url}`;
      setPosterUrl(fullUrl);
      toast.success("Poster uploaded");
    } catch (e) {
      toast.error(e.message || "Poster upload failed");
    } finally { setPosterUploading(false); }
  };

  const onFilmChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilmUploading(true); setFilmProg(0);
    try {
      const r = await uploadFile(file, "festival_film", setFilmProg);
      setFilmPath(r.path);
      toast.success("Film uploaded");
    } catch (e) {
      toast.error(e.message || "Film upload failed");
    } finally { setFilmUploading(false); }
  };

  const submit = async () => {
    if (!title.trim() || !festivalId) { toast.error("Title + festival required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/festival/submissions", {
        festival_id: festivalId,
        title, synopsis,
        runtime_min: parseInt(runtime) || 0,
        region, country: country || undefined, language,
        genres: genres.split(",").map((s) => s.trim()).filter(Boolean),
        youtube_trailer_id: trailerId || undefined,
        poster_url: posterUrl || undefined,
        film_path: filmPath || undefined,
        parent_submission_id: parentSub?.submission_id || undefined,
        collaborators: collabs.map((c) => ({ user_id: c.user_id, role: c.role })),
      });
      toast.success(parentSub ? "Resubmitted!" : "Submission created!");
      // If the festival charges a submission fee and it hasn't been paid/waived, route to checkout immediately.
      if (data?.status === "pending_payment" && !data.submission_fee_paid && !data.submission_fee_waived) {
        try {
          toast.message(`Submission fee due: $${data.submission_fee_amount} ${(data.submission_fee_currency || "usd").toUpperCase()} (${data.submission_fee_tier})`);
          const { data: checkout } = await api.post(
            `/festival/submissions/${data.submission_id}/fee-checkout`,
            { origin_url: window.location.origin }
          );
          if (checkout?.url) {
            window.location.href = checkout.url;
            return;
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not start checkout — find your film in the dashboard to pay later");
        }
      }
      nav(`/festival/films/${data.submission_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const addCollab = async () => {
    const email = collabEmail.trim().toLowerCase();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) { toast.error("Valid email required"); return; }
    if (collabs.length >= 5) { toast.error("Max 5 collaborators"); return; }
    setCollabAdding(true);
    try {
      const { data } = await api.get(`/festival/users/lookup?email=${encodeURIComponent(email)}`);
      if (collabs.find((c) => c.user_id === data.user_id) || data.user_id === user?.user_id) {
        toast.error("Already added"); return;
      }
      setCollabs([...collabs, { user_id: data.user_id, name: data.name, role: collabRole }]);
      setCollabEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "User not found");
    } finally { setCollabAdding(false); }
  };

  const removeCollab = (uid) => setCollabs(collabs.filter((c) => c.user_id !== uid));

  if (!user) return (
    <div className="min-h-screen bg-[#0A0A0A]"><Navbar /></div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-3xl mx-auto pb-20">
        <h1 className="text-4xl font-black mb-2">{parentSub ? "Edit & Resubmit" : "Submit Your Film"}</h1>
        <p className="text-gray-400 text-sm mb-8">Open submissions · Audience-voted leaderboard</p>

        {parentSub && (
          <div data-testid="resubmit-banner" className="mb-6 bg-gradient-to-r from-amber-900/30 to-[#1a0d00] border border-amber-500/40 rounded-md p-4 text-sm flex items-start gap-3">
            <RotateCcw className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Resubmitting <span className="text-white">{parentSub.title}</span></p>
              {parentSub.rejected_reason && (
                <p className="text-amber-200/80 text-xs leading-relaxed">
                  Previous moderator note: <span className="italic">"{parentSub.rejected_reason}"</span>
                </p>
              )}
              <p className="text-gray-500 text-[11px] mt-1.5">All fields are pre-filled — edit any details, then submit. The original entry is kept for audit.</p>
            </div>
          </div>
        )}
        {step === 0 && (
          <div data-testid="filmmaker-apply" className="bg-[#141414] border border-white/5 rounded-lg p-8">
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-amber-400">
              <Film className="w-3.5 h-3.5" /> Step 1 of 2 — Become a Filmmaker
            </div>
            <h2 className="text-2xl font-bold mb-4">Tell us about you</h2>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1 mt-3">Short bio</label>
            <textarea data-testid="bio-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="3-line bio…" className="input-dark w-full" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Country (ISO-2)</label>
                <input data-testid="country-input" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="IN" className="input-dark w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Website</label>
                <input data-testid="website-input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" className="input-dark w-full" />
              </div>
            </div>
            <button data-testid="apply-btn" onClick={apply} disabled={applying} className="btn-red px-6 py-3 rounded font-semibold mt-6 inline-flex items-center gap-2 disabled:opacity-50">
              {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        {step === 1 && (
          <div data-testid="submission-form" className="space-y-5">
            <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">Film details</h2>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Festival</label>
              <select data-testid="festival-select" value={festivalId} onChange={(e) => setFestivalId(e.target.value)} className="input-dark w-full mb-4">
                {festivals.map((f) => (<option key={f.festival_id} value={f.festival_id}>{f.name}</option>))}
              </select>
              {(() => {
                const sel = festivals.find((f) => f.festival_id === festivalId);
                if (!sel || !sel.submission_fee_short) return null;
                const rt = parseInt(runtime) || 0;
                const cutoff = sel.short_film_max_min || 25;
                const isShort = rt > 0 && rt <= cutoff;
                const baseFee = isShort ? sel.submission_fee_short : sel.submission_fee_feature;
                const tier = isShort ? "Short" : (rt === 0 ? "—" : "Feature");
                const cur = (sel.submission_fee_currency || "usd").toUpperCase();
                const ebRemaining = quote?.early_bird_slots_remaining ?? null;
                const ebActive = quote?.early_bird === true;
                const finalFee = quote?.amount ?? baseFee;
                return (
                  <div data-testid="fee-preview" className="bg-amber-500/5 border border-amber-500/25 rounded-lg p-3 mb-4 flex items-start gap-3">
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded bg-amber-500/15 text-amber-300 font-black shrink-0">$</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                        Submission fee:
                        {ebActive && finalFee !== baseFee ? (
                          <>
                            <span className="line-through text-gray-500 text-xs">${baseFee.toFixed(2)}</span>
                            <span className="text-amber-300">${finalFee.toFixed(2)} {cur}</span>
                            <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold">
                              🐦 Early Bird · {quote.discount_pct}% off
                            </span>
                          </>
                        ) : (
                          <span className="text-amber-300">${finalFee.toFixed(2)} {cur}</span>
                        )}
                        <span className="text-xs text-gray-400 font-normal">({tier} tier)</span>
                      </div>
                      {ebActive && ebRemaining !== null && (
                        <div data-testid="early-bird-slots" className="text-[11px] text-amber-400/90 mt-1 font-semibold">
                          Only {ebRemaining} early-bird slot{ebRemaining === 1 ? "" : "s"} left in this festival!
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500 mt-1">
                        Short (≤{cutoff} min) = ${sel.submission_fee_short.toFixed(2)}. Anything longer = ${sel.submission_fee_feature.toFixed(2)}. Paid via Stripe after you click Submit.
                      </div>
                    </div>
                  </div>
                );
              })()}
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Title *</label>
              <input data-testid="title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark w-full mb-3" />
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Synopsis</label>
              <textarea data-testid="synopsis-input" rows={4} value={synopsis} onChange={(e) => setSynopsis(e.target.value)} className="input-dark w-full" />
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Runtime (min)</label>
                  <input data-testid="runtime-input" type="number" min="0" value={runtime} onChange={(e) => setRuntime(e.target.value)} className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Region</label>
                  <select data-testid="region-select" value={region} onChange={(e) => setRegion(e.target.value)} className="input-dark w-full">
                    {REGIONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Language</label>
                  <input value={language} onChange={(e) => setLanguage(e.target.value)} className="input-dark w-full" />
                </div>
              </div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1 mt-3">Genres (comma-separated)</label>
              <input data-testid="genres-input" value={genres} onChange={(e) => setGenres(e.target.value)} placeholder="drama, short, experimental" className="input-dark w-full" />
            </div>

            <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">Trailer (public preview)</h2>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">YouTube ID</label>
              <input data-testid="trailer-input" value={trailerId} onChange={(e) => setTrailerId(e.target.value)} placeholder="dQw4w9WgXcQ" className="input-dark w-full" />
              <p className="text-[11px] text-gray-500 mt-1">Just the ID — anything after <code>v=</code> in the YouTube URL.</p>
            </div>

            <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-1">Collaborators (optional)</h2>
              <p className="text-xs text-gray-500 mb-4">Credit up to 5 collaborators. They'll see this film on their dashboard, and their followers will get notified when you publish.</p>
              {collabs.length > 0 && (
                <div className="space-y-2 mb-4">
                  {collabs.map((c) => (
                    <div key={c.user_id} data-testid={`collab-row-${c.user_id}`} className="flex items-center gap-3 bg-black/40 border border-white/5 rounded p-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-700/40 to-[#E50914]/40 flex items-center justify-center text-xs font-black flex-shrink-0">
                        {(c.name || "F").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{c.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-400">{c.role}</div>
                      </div>
                      <button onClick={() => removeCollab(c.user_id)} aria-label="Remove" className="text-gray-500 hover:text-red-400 p-1">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {collabs.length < 5 && (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2">
                  <input
                    data-testid="collab-email-input"
                    type="email"
                    value={collabEmail}
                    onChange={(e) => setCollabEmail(e.target.value)}
                    placeholder="collaborator@email.com"
                    className="input-dark"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCollab())}
                  />
                  <select data-testid="collab-role-select" value={collabRole} onChange={(e) => setCollabRole(e.target.value)} className="input-dark">
                    <option value="co-director">Co-director</option>
                    <option value="writer">Writer</option>
                    <option value="cinematographer">Cinematographer</option>
                    <option value="editor">Editor</option>
                    <option value="composer">Composer</option>
                    <option value="producer">Producer</option>
                    <option value="other">Other</option>
                  </select>
                  <button data-testid="collab-add-btn" onClick={addCollab} disabled={collabAdding || !collabEmail} className="btn-glass px-4 py-2 rounded font-semibold inline-flex items-center gap-2 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50">
                    {collabAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
                  </button>
                </div>
              )}
            </div>

            <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">Poster</h2>
              <div className="flex items-center gap-4">
                {posterUrl && <img src={posterUrl} alt="" className="w-20 h-28 object-cover rounded" />}
                <button data-testid="poster-upload-btn" onClick={() => posterInputRef.current?.click()} disabled={posterUploading} className="btn-glass px-5 py-2 rounded font-semibold inline-flex items-center gap-2 border border-white/15">
                  {posterUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {posterUrl ? "Replace poster" : "Upload poster"}
                </button>
                <input ref={posterInputRef} type="file" accept="image/*" onChange={onPosterChange} className="hidden" />
              </div>
            </div>

            <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">Full film (paywalled)</h2>
              <p className="text-xs text-gray-500 mb-3">MP4 / WebM / MOV up to 500MB. Audience must purchase a ticket to watch.</p>
              <div className="flex items-center gap-4">
                {filmPath && !filmUploading && <Check className="w-5 h-5 text-green-400" />}
                <button data-testid="film-upload-btn" onClick={() => filmInputRef.current?.click()} disabled={filmUploading} className="btn-glass px-5 py-2 rounded font-semibold inline-flex items-center gap-2 border border-white/15">
                  {filmUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {filmPath ? "Replace film" : "Upload film"}
                </button>
                <input ref={filmInputRef} type="file" accept="video/*" onChange={onFilmChange} className="hidden" />
                {filmUploading && <span className="text-xs text-gray-400">{filmProg}%</span>}
              </div>
              {filmUploading && (
                <div className="mt-3 bg-black/50 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-[#E50914] h-full transition-all" style={{ width: `${filmProg}%` }} />
                </div>
              )}
            </div>

            <button data-testid="submission-submit-btn" onClick={submit} disabled={submitting} className="btn-red w-full py-4 rounded font-bold text-base disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Film className="w-4 h-4" /> Submit film</>}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
